import * as React from 'react';
import { Component } from 'react';
import { Redirect } from 'react-router-dom';
import {
  Box,
  Button,
  Flashbar,
  FlashbarProps,
  Modal,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3';
import { PageHeader, RequestInformation } from './common';
import {
  approveRequest,
  cancelRequest,
  denyRequest,
  getRequest,
} from '../../../api/permissions';
import { Page } from '../../../routes/Paths';

export interface PermissionRequestDetailsProps {
  setContentType: any;
  match: any;
  activeGroup: string;
}

export interface PermissionRequestDetailsState {
  items: any;
  notifications: FlashbarProps.MessageDefinition[];
  action: string;
  actionLoading: boolean;
  buttonLoading: boolean;
  requestLoading: boolean;
  redirect: string;
  modalVisible: boolean;
  isInvalid: boolean;
  modalText: string;
  request: any;
  textAreaPlaceholder: string;
  reasonOfAction: any;
}

export class PermissionRequestDetails extends Component<
  PermissionRequestDetailsProps,
  PermissionRequestDetailsState
> {
  state = {
    items: [],
    notifications: [],
    action: undefined,
    actionLoading: false,
    buttonLoading: false,
    requestLoading: false,
    redirect: undefined,
    modalVisible: false,
    isInvalid: false,
    reasonOfAction: undefined,
    request: '',
    modalText: undefined,
    alertMessage: '',
    textAreaPlaceholder: undefined,
  };

  componentDidMount = async () => {
    this.props.setContentType('table');
    await this.handleRefresh();
  };

  componentDidUpdate = async (prevProps) => {
    if (prevProps.activeGroup !== this.props.activeGroup) {
      await this.handleRefresh();
    }
  };

  transformData(item) {
    const transform = {};
    transform['requestId'] = item.requestId;
    transform['principal'] = item.dataLakePrincipal;
    transform['status'] = item.status;
    transform['owner'] = item.groupRequestedTo;
    transform['requestGroup'] = item.groupRequestedBy;
    transform['requestedBy'] = item.requestedBy;
    transform['decidedBy'] = item.decidedBy;
    transform['timeOfRequest'] = item.timeOfRequest;
    transform['timeOfDecision'] = item.timeOfDecision;
    transform['useCase'] = item.useCase;
    transform['reasonOfAction'] = item.reasonOfAction;
    transform['permissions'] = JSON.parse(
      item.lakeFormationRequest.PermissionsString,
    );
    transform['grantablePermissions'] = JSON.parse(
      item.lakeFormationRequest.PermissionsWithGrantOptionString,
    );
    transform['resource'] = JSON.parse(
      item.lakeFormationRequest.ResourceString,
    );
    if (transform['resource'].table) {
      transform['type'] = 'table';
      transform['catalogId'] = transform['resource'].table.catalogId;
      transform['databaseName'] = transform['resource'].table.databaseName;
      transform['resource'].table.tableWildcard
        ? (transform['tableName'] = 'All Tables')
        : (transform['tableName'] = transform['resource'].table.name);
      transform['columnNames'] = 'All Columns';
    } else {
      transform['type'] = 'column';
      transform['catalogId'] = transform['resource'].tableWithColumns.catalogId;
      transform['databaseName'] =
        transform['resource'].tableWithColumns.databaseName;
      transform['tableName'] = transform['resource'].tableWithColumns.name;
      if (transform['resource'].tableWithColumns.columnNames) {
        transform['columnNames'] =
          transform['resource'].tableWithColumns.columnNames.join(', ');
        transform['columnWildCard'] = 'Include';
      } else {
        transform['columnNames'] =
          transform[
            'resource'
          ].tableWithColumns.columnWildcard.excludedColumnNames.join(', ');
        transform['columnWildCard'] = 'Exclude';
      }
    }
    return transform;
  }

  handleRefresh = async () => {
    this.closeModal();
    if (!this.props.activeGroup) return;
    let requestResult;
    try {
      requestResult = await getRequest({
        requestId: this.props.match.params.id,
      });
      this.setState({
        request: this.transformData(requestResult.requestItem),
      });
    } catch (err) {
      console.log(err);
    }
  };

  openModal(clickedAction) {
    this.setState({ modalVisible: true, action: clickedAction });
  }

  closeModal() {
    this.setState({
      modalVisible: false,
      action: undefined,
      buttonLoading: false,
    });
  }

  successMessage(message, action) {
    if (action === 'approved') {
      this.setState({
        notifications: [
          {
            type: 'success',
            content: `Lake Formation '${message}' was '${action}'`,
            dismissible: true,
            buttonText: 'View datasets',
            onButtonClick: () =>
              this.setState({
                redirect: Page.MY_DATASETS,
              }),
            onDismiss: () => this.setState({ notifications: [] }),
          },
        ],
      });
    } else {
      this.setState({
        notifications: [
          {
            type: 'success',
            content: `Lake Formation '${message}' was '${action}'`,
            dismissible: true,
            onDismiss: () => this.setState({ notifications: [] }),
          },
        ],
      });
    }
  }

  errorMessage(message, action) {
    this.setState({
      notifications: [
        {
          type: 'error',
          content: `There was an error '${action}' your Lake Formation request: '${message}'`,
          dismissible: true,
          onDismiss: () => this.setState({ notifications: [] }),
        },
      ],
    });
  }

  submitAction = async (action) => {
    if (action === 'cancel') {
      if (!this.state.reasonOfAction) {
        this.setState({ isInvalid: true });
        return;
      }
      this.setState({ buttonLoading: true });
      try {
        await cancelRequest({
          requestId: this.state.request['requestId'],
          reason: this.state.reasonOfAction,
        });
        this.successMessage(this.state.request['requestId'], 'cancelled');
        await this.handleRefresh();
      } catch (err) {
        console.log(err);
        this.errorMessage(err.message, 'canceling');
      }
    } else if (action === 'approve') {
      this.setState({ actionLoading: true });
      try {
        await approveRequest({
          requestId: this.state.request['requestId'],
        });
        this.successMessage(this.state.request['requestId'], 'approved');
        await this.handleRefresh();
      } catch (err) {
        console.log(err);
        this.errorMessage(err.message, 'approving');
      }
      this.setState({ actionLoading: false });
    } else if (action === 'deny') {
      if (!this.state.reasonOfAction) {
        this.setState({ isInvalid: true });
        return;
      }
      this.setState({ buttonLoading: true });
      try {
        await denyRequest({
          requestId: this.state.request['requestId'],
          reason: this.state.reasonOfAction,
        });
        this.successMessage(this.state.request['requestId'], 'denied');
        await this.handleRefresh();
      } catch (err) {
        console.log(err);
        this.errorMessage(err.message, 'denying');
      }
    }
  };

  handleAction = async (e) => {
    if (e.detail.id === 'cancel') {
      this.openModal('cancel');
    } else if (e.detail.id === 'approve') {
      await this.submitAction(e.detail.id);
    } else if (e.detail.id === 'deny') {
      this.openModal('deny');
    } else if (e.detail.id === 'view') {
      await this.submitAction(e.detail.id);
    }
  };

  options() {
    let actionItems = [];
    if (this.props.activeGroup === this.state.request['requestGroup']) {
      actionItems = [
        {
          text: 'Cancel',
          id: 'cancel',
          disabled: this.state.request['status'] !== 'PENDING',
        },
      ];
    }
    if (this.props.activeGroup === this.state.request['owner']) {
      actionItems.push(
        {
          text: 'Approve',
          id: 'approve',
          disabled: this.state.request['status'] !== 'PENDING',
        },
        {
          text: 'Deny',
          id: 'deny',
          disabled: this.state.request['status'] !== 'PENDING',
        },
      );
    }
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: this.handleRefresh,
      },
      {
        text: 'Actions',
        onItemClick: this.handleAction,
        loading: this.state.actionLoading,
        items: actionItems,
      },
    ];
  }

  render() {
    if (this.state.redirect) {
      return <Redirect push to={this.state.redirect} />;
    }
    return (
      <SpaceBetween size='xs'>
        <Flashbar items={this.state.notifications} />
        <br />
        <Modal
          visible={this.state.modalVisible}
          header={
            this.state.action === 'cancel'
              ? 'Enter reason for canceling'
              : 'Enter reason for denying'
          }
          onDismiss={() => this.closeModal()}
          footer={
            <Box float='right'>
              <SpaceBetween direction='horizontal' size='xs'>
                <Button variant='link' onClick={() => this.closeModal()}>
                  No
                </Button>
                <Button
                  variant='primary'
                  loading={this.state.buttonLoading}
                  onClick={() => this.submitAction(this.state.action)}
                >
                  Yes
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <Textarea
            value={this.state.reasonOfAction}
            onChange={({ detail }) =>
              this.setState({ isInvalid: false, reasonOfAction: detail.value })
            }
            placeholder={this.state.textAreaPlaceholder}
            invalid={this.state.isInvalid}
          />
          Are you sure you want to {this.state.action} this request?
        </Modal>
        <PageHeader
          buttons={this.options()}
          header={`Request ID: ${this.props.match.params.id}`}
        />
        <RequestInformation request={this.state.request} />
      </SpaceBetween>
    );
  }
}
